import { Link } from "react-router-dom";
import {
  Phone,
  AlertTriangle,
  Clock,
  Check,
  DoorOpen,
  KeyRound,
  ShieldAlert,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { COMPANY, INTERVENTION_CITIES, HERO_IMAGE } from "../lib/constants";
import useSeo, { breadcrumbJsonLd, SITE } from "../lib/useSeo";
import InsurancePartners from "../components/InsurancePartners";

const STEPS = [
  {
    t: "Vous nous appelez",
    d: "Décrivez-nous votre situation, nous estimons le temps d'intervention et le tarif avant le déplacement.",
  },
  {
    t: "Nous arrivons",
    d: "En moyenne sous 30 minutes dans tout le secteur Mont-Dol / Côte d'Émeraude / 22 / 35.",
  },
  {
    t: "Diagnostic & devis",
    d: "Tarif annoncé clairement avant toute intervention, zéro surprise.",
  },
  {
    t: "Intervention soignée",
    d: "Ouverture fine privilégiée, puis remise en état ou changement si nécessaire.",
  },
];

const SITUATIONS = [
  { icon: DoorOpen, t: "Porte claquée", d: "Ouverture non destructive." },
  { icon: KeyRound, t: "Clé perdue / cassée", d: "Extraction, reproduction, changement." },
  { icon: ShieldAlert, t: "Tentative d'effraction", d: "Mise en sécurité immédiate." },
  { icon: Clock, t: "Impossible de rentrer", d: "Intervention toute heure, nuit & WE." },
];

export default function Emergency() {
  useSeo({
    title:
      "Dépannage serrurerie urgence 24h/24 — Intervention 30 min | Mon Serrurier Breton",
    description:
      "Porte claquée, clé perdue, effraction ? Serrurier d'urgence disponible 24h/24, 7j/7 en Bretagne (22 & 35). Intervention en 30 minutes, ouverture sans casse, tarifs annoncés avant l'intervention.",
    path: "/urgence",
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Accueil", path: "/" },
        { name: "Dépannage / Urgence", path: "/urgence" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${SITE.url}/urgence#service`,
        name: "Dépannage serrurerie urgence 24h/24",
        serviceType: "Emergency locksmith",
        provider: { "@id": `${SITE.url}/#business` },
        areaServed: [
          { "@type": "AdministrativeArea", name: "Côtes-d'Armor (22)" },
          { "@type": "AdministrativeArea", name: "Ille-et-Vilaine (35)" },
        ],
        availableChannel: {
          "@type": "ServiceChannel",
          servicePhone: "+33780939300",
          availableLanguage: ["French"],
        },
        hoursAvailable: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ],
          opens: "00:00",
          closes: "23:59",
        },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Prestations d'urgence",
          itemListElement: [
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Ouverture de porte claquée" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Ouverture après perte de clés" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mise en sécurité après effraction" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Extraction de clé cassée" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Serrure bloquée / grippée" } },
          ],
        },
      },
    ],
  });
  return (
    <div data-testid="page-emergency">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 space-y-6">
            <div
              data-testid="emergency-badge"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700"
            >
              <span className="h-px w-8 bg-teal-700" />
              Urgence · Dépannage 24h/24
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
              Porte claquée ?
              <br />
              <span className="text-teal-600">On arrive en 30 min.</span>
            </h1>
            <p className="text-slate-600 text-lg max-w-xl leading-relaxed">
              Un serrurier artisan, diplômé, local. Pas d'intermédiaire, pas de
              surfacturation : un appel et une intervention rapide, 7j/7, même
              la nuit et les jours fériés.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                data-testid="emergency-hero-call"
                href={`tel:${COMPANY.phoneDigits}`}
                className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-8 py-5 text-xl shadow-2xl shadow-orange-600/30 pulse-emergency"
              >
                <Phone className="w-6 h-6" />
                {COMPANY.phone}
              </a>
              <Link
                data-testid="emergency-hero-contact"
                to="/contact"
                className="inline-flex items-center gap-2 bg-white border-2 border-slate-900 hover:bg-slate-900 hover:text-white text-slate-900 font-bold rounded-full px-6 py-5 transition-colors"
              >
                Formulaire de contact <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 pt-4 text-sm text-slate-700 border-t border-slate-200">
              <span className="flex items-center gap-2 pt-3">
                <Check className="w-4 h-4 text-teal-600" /> Devis gratuit
              </span>
              <span className="flex items-center gap-2 pt-3">
                <Check className="w-4 h-4 text-teal-600" /> Artisan diplômé
              </span>
              <span className="flex items-center gap-2 pt-3">
                <Check className="w-4 h-4 text-teal-600" /> Assurance acceptée
              </span>
              <span className="flex items-center gap-2 pt-3">
                <Check className="w-4 h-4 text-teal-600" /> Travail garanti
              </span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div
              data-testid="emergency-hero-image-wrap"
              className="relative rounded-[2rem] overflow-hidden border border-slate-200 shadow-2xl shadow-orange-900/10 bg-white"
            >
              <img
                src={HERO_IMAGE}
                alt="Dépannage serrurerie d'urgence : ouverture de cylindre par perçage technique"
                className="w-full h-[460px] md:h-[520px] object-cover"
                style={{ objectPosition: "40% 50%" }}
              />
              <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-lg flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-600 text-white flex items-center justify-center pulse-emergency">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
                    Urgence 24h/24
                  </div>
                  <div className="font-display font-extrabold text-slate-900">
                    Sur place en 30 min
                  </div>
                </div>
              </div>
              <div className="absolute bottom-4 right-4 bg-slate-900 text-white rounded-2xl px-4 py-3 shadow-lg flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal-500 text-white flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider">
                    Ouverture
                  </div>
                  <div className="font-display font-extrabold text-lg leading-none">
                    sans casse privilégiée
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ligne urgence + situations */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-12">
        <div className="rounded-3xl bg-white border border-slate-200 p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="w-14 h-14 rounded-full bg-orange-600 text-white flex items-center justify-center flex-shrink-0">
              <Phone className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="text-xs text-slate-500 uppercase tracking-widest font-bold">
                Ligne urgence
              </div>
              <a
                href={`tel:${COMPANY.phoneDigits}`}
                data-testid="emergency-card-phone"
                className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 hover:text-teal-700 transition-colors"
              >
                {COMPANY.phone}
              </a>
            </div>
            <a
              href={`tel:${COMPANY.phoneDigits}`}
              data-testid="emergency-card-call"
              className="hidden md:inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-full px-6 py-3 transition-colors"
            >
              <Phone className="w-4 h-4" /> Appeler maintenant
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SITUATIONS.map((s) => (
              <div
                key={s.t}
                className="flex items-start gap-3 bg-slate-50 rounded-2xl p-4 border border-slate-100"
              >
                <s.icon className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-bold text-slate-900">{s.t}</div>
                  <div className="text-sm text-slate-600">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
            <span className="h-px w-8 bg-teal-700" /> Comment ça marche
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Un processus simple,
            <br />
            <span className="text-teal-600">rapide et transparent.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div
              key={i}
              data-testid={`emergency-step-${i + 1}`}
              className="bg-white border border-slate-200 rounded-3xl p-7 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-4 font-display font-black text-slate-100 text-8xl leading-none select-none">
                {i + 1}
              </div>
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-teal-600 text-white flex items-center justify-center mb-4 font-display font-extrabold">
                  {i + 1}
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-2">
                  {s.t}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tariff reassurance */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-teal-600 text-white rounded-3xl p-8">
            <Clock className="w-8 h-8 mb-4" />
            <div className="font-display text-4xl font-extrabold mb-2">
              30 min
            </div>
            <p className="text-teal-100">
              Temps d'intervention moyen dans tout le secteur Mont-Dol /
              Côte d'Émeraude /
              22 / 35.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-3xl p-8">
            <Check className="w-8 h-8 text-teal-600 mb-4" />
            <div className="font-display text-4xl font-extrabold text-slate-900 mb-2">
              0 €
            </div>
            <p className="text-slate-600">
              Pas de frais de déplacement surprises. Devis annoncé avant
              intervention.
            </p>
          </div>
          <div className="bg-slate-900 text-white rounded-3xl p-8">
            <AlertTriangle className="w-8 h-8 text-amber-300 mb-4" />
            <div className="font-display text-4xl font-extrabold mb-2">
              24 / 7
            </div>
            <p className="text-slate-300">
              Disponibles jour, nuit, week-ends et jours fériés.
            </p>
          </div>
        </div>
      </section>

      {/* Insurance partners */}
      <InsurancePartners />

      {/* Zone */}
      <section
        data-testid="emergency-zone"
        className="max-w-7xl mx-auto px-4 md:px-8 pb-20"
      >
        <div className="rounded-3xl bg-white border border-slate-200 p-8 md:p-12">
          <div className="flex items-start gap-3 mb-6">
            <MapPin className="w-6 h-6 text-teal-600 mt-1" />
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">
                On intervient dans votre ville
              </h2>
              <p className="text-slate-600 mt-1">
                Rayon d'environ {COMPANY.radiusKm} km autour de
                Mont-Dol (35120).
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {INTERVENTION_CITIES.map((c) => {
              const slug = c
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "");
              return (
                <Link
                  key={c}
                  to={`/serrurier-${slug}`}
                  className="bg-slate-50 hover:bg-teal-50 hover:text-teal-700 border border-slate-200 px-4 py-2 rounded-full text-sm font-semibold text-slate-700 transition-colors"
                >
                  {c}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="rounded-3xl bg-orange-600 text-white p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-amber-300/30 blur-3xl" />
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-widest text-orange-100 mb-3">
              Dépannage ouverture de porte 24h/24 — 7j/7
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight leading-tight mb-6">
              N'attendez pas. Un appel, on est là.
            </h2>
            <a
              data-testid="emergency-final-call"
              href={`tel:${COMPANY.phoneDigits}`}
              className="inline-flex items-center gap-3 bg-white text-orange-700 font-bold rounded-full px-10 py-6 text-2xl shadow-2xl hover:bg-amber-100 transition-colors"
            >
              <Phone className="w-7 h-7" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
